import React, { useState } from 'react';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import {
	LOGO_ACCEPT_TYPES,
	LOGO_ACCEPT_TYPE_LABELS,
	LOGO_MAX_SIZE,
} from 'utils';
import { isFileFormatValid, isFileSizeValid, uploadFileToDB } from 'helpers';
import { ILogo } from 'modules/admin-panel/models';
import { Dropzone, SingleUpload } from 'components/drop-zone/Dropzone';
import { IClientFormFields } from 'modules/admin-panel/pages/clients/models';

interface LogoDropzoneProps {
	wrapperClass?: string;
	onUploadFail: (e: Error) => void;
	onUpload: (name: string, logo?: ILogo) => void;
	uploadedData?: ILogo;
	logoId?: string;
}

const logoEndpoint = '/clients/logo';

export function LogoDropzone({
	wrapperClass,
	onUploadFail,
	onUpload,
	uploadedData,
	logoId = IClientFormFields.logo,
}: LogoDropzoneProps) {
	const { setValue, setError, clearError } = useFormContext();
	const { t: te } = useTranslation('errors');
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const handleUploadLogo = (newLogo: File) => {
		if (!isFileSizeValid(newLogo.size, LOGO_MAX_SIZE)) {
			return setError(
				logoId,
				'custom',
				te('file_size_not_more', {
					size: LOGO_MAX_SIZE,
				}),
			);
		}

		if (!isFileFormatValid(newLogo.name, LOGO_ACCEPT_TYPE_LABELS)) {
			return setError(logoId, 'custom', te('format_not_supported'));
		}

		setIsLoading(true);
		onChangeLogo({
			fileKey: 'temp',
			versionId: 'temp',
			publicUrl: URL.createObjectURL(newLogo),
		});
		clearError(logoId);

		uploadFileToDB(newLogo, logoEndpoint, IClientFormFields.logo)
			.then((res: AxiosResponse<ILogo>) => onChangeLogo(res.data))
			.catch((e: Error) => {
				onChangeLogo();
				// check error code or message
				// setError(e.code);
				onUploadFail(e);
			});

		setIsLoading(false);
	};

	const handleDeleteLogo = () => {
		onChangeLogo();
	};

	const renderUploadedData = (uploadedLogo: ILogo) => {
		return (
			<>
				<img
					src={uploadedLogo.publicUrl}
					alt="UserPhoto"
					className="new-logo"
				/>
			</>
		);
	};

	function onChangeLogo(newLogo?: ILogo) {
		setValue(logoId, newLogo?.publicUrl);
		onUpload(logoId, newLogo);
	}

	return (
		<Dropzone<SingleUpload<ILogo>>
			wrapperClass={wrapperClass}
			renderUploadedData={renderUploadedData}
			acceptTypes={LOGO_ACCEPT_TYPES}
			acceptTypesLabels={LOGO_ACCEPT_TYPE_LABELS}
			onUpload={handleUploadLogo}
			onDeleteUploadedData={handleDeleteLogo}
			uploadedData={uploadedData}
			id={logoId}
			isLoading={isLoading}
			required
		/>
	);
}
